import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { Prose } from "@/components/site/Prose";
import { ChevronRight, Compass, Waves, Landmark, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_FALLBACK = "/home_hero.png";
const DEFAULT_FEATURES = [
  "https://images.unsplash.com/photo-1763390097867-02d96b56599a?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  "https://images.unsplash.com/photo-1629670670137-c0a865ad29ce?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  "https://images.unsplash.com/photo-1633978077821-6b1b16a176a4?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  "https://images.unsplash.com/photo-1721444694627-8cabea39967e?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
];

export default function HomePage() {
  const { t, content } = useI18n();
  const hero = content?.intro?.image_url || HERO_FALLBACK;
  const [features, setFeatures] = useState(DEFAULT_FEATURES);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/images/home_features`).then((r) => {
      if (cancelled) return;
      const arr = r?.data?.images;
      if (Array.isArray(arr) && arr.length === 4) setFeatures(arr);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [FORTRESS, BEACH, REEF, TOWN] = features;
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] overflow-hidden grain" data-testid="hero">
        <div className="absolute inset-0 hero-bg" style={{ backgroundImage: `url(${hero})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[var(--bg-base)]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-28 lg:pt-40 pb-20">
          <p className="text-xs uppercase tracking-[0.4em] text-white/85 font-bold mb-6 fade-up">{t("hero_eyebrow")}</p>
          <h1 className="font-display text-white text-5xl sm:text-7xl lg:text-[8rem] leading-[0.95] tracking-tight fade-up">
            {t("hero_title")}
          </h1>
          <p className="mt-8 max-w-2xl text-white/90 text-lg sm:text-xl leading-relaxed fade-up">
            {t("hero_subtitle")}
          </p>
          <div className="mt-10 flex flex-wrap gap-3 fade-up">
            <Link
              to="/history"
              className="inline-flex items-center gap-2 rounded-full bg-white text-stone-900 ps-6 pe-5 h-12 text-sm font-medium hover:bg-[var(--brand-accent)] transition"
              data-testid="hero-cta-primary"
            >
              {t("hero_cta_primary")} <ChevronRight className="h-4 w-4" />
            </Link>
            <Link
              to="/directory"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white ps-6 pe-5 h-12 text-sm font-medium hover:bg-white/10 transition"
              data-testid="hero-cta-secondary"
            >
              {t("hero_cta_secondary")}
            </Link>
          </div>
        </div>
        <div className="absolute bottom-6 inset-x-0 mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between text-white/60 text-xs">
          <span>26.0989° N · 34.2786° E</span>
          <span className="hidden md:block">scroll</span>
        </div>
      </section>

      {/* INTRO */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="intro-section">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--brand-primary)] mb-4">{t("home_eyebrow_welcome")}</p>
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-stone-900 leading-[1.05]">
            {t("home_intro_heading")}
          </h2>
        </div>
        <div className="lg:col-span-7">
          <Prose section="intro" fallbackTitle={t("hero_title")} />
        </div>
      </section>

      {/* TETRIS BENTO */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24" data-testid="bento-section">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--brand-primary)] mb-4">{t("home_eyebrow_discover")}</p>
        <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-stone-900 leading-[1.05] mb-12 max-w-2xl">
          {t("home_discover_heading")}
        </h2>

        <div className="tetris grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5 lg:gap-7">
          <Link to="/history" className="md:col-span-5 lg:col-span-7 relative rounded-3xl overflow-hidden bg-stone-900 aspect-[16/10] group" data-testid="card-history">
            <img src={FORTRESS} alt="Ottoman fortress" className="absolute inset-0 h-full w-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-8 text-white">
              <Landmark className="h-5 w-5 mb-3 opacity-80" />
              <h3 className="font-display text-3xl">{t("section_history")}</h3>
              <p className="text-sm text-white/75 mt-2 max-w-md">{t("home_history_blurb")}</p>
            </div>
          </Link>

          <Link to="/beaches" className="md:col-span-3 lg:col-span-5 relative rounded-3xl overflow-hidden bg-stone-900 aspect-[16/10] group" data-testid="card-beaches">
            <img src={BEACH} alt="Beach" className="absolute inset-0 h-full w-full object-cover opacity-90 group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-8 text-white">
              <Waves className="h-5 w-5 mb-3 opacity-80" />
              <h3 className="font-display text-3xl">{t("section_beaches")}</h3>
              <p className="text-sm text-white/75 mt-2">{t("home_beaches_blurb")}</p>
            </div>
          </Link>

          <Link to="/beaches" className="md:col-span-4 lg:col-span-5 relative rounded-3xl overflow-hidden bg-stone-900 aspect-[16/12] group" data-testid="card-diving">
            <img src={REEF} alt="Diving" className="absolute inset-0 h-full w-full object-cover opacity-90 group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-8 text-white">
              <Compass className="h-5 w-5 mb-3 opacity-80" />
              <h3 className="font-display text-3xl">{t("section_diving")}</h3>
              <p className="text-sm text-white/75 mt-2">{t("home_diving_blurb")}</p>
            </div>
          </Link>

          <Link to="/sights" className="md:col-span-4 lg:col-span-7 relative rounded-3xl overflow-hidden bg-stone-900 aspect-[16/12] group" data-testid="card-sights">
            <img src={TOWN} alt="Old town" className="absolute inset-0 h-full w-full object-cover opacity-90 group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-8 text-white">
              <Camera className="h-5 w-5 mb-3 opacity-80" />
              <h3 className="font-display text-3xl">{t("section_sights")}</h3>
              <p className="text-sm text-white/75 mt-2">{t("home_sights_blurb")}</p>
            </div>
          </Link>
        </div>
      </section>

      {/* ADVERTISE STRIP */}
      <section className="bg-[#0A2540] text-white" data-testid="cta-strip">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--brand-accent)] font-bold mb-4">{t("home_for_business")}</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">{t("advertise_title")}</h2>
            <p className="mt-4 text-stone-300 max-w-2xl">{t("advertise_subtitle")}</p>
          </div>
          <div className="md:col-span-4 md:text-end">
            <Link to="/advertise" className="inline-flex items-center gap-2 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-full ps-6 pe-5 h-12 text-sm font-medium transition" data-testid="cta-advertise">
              {t("nav_advertise")} <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
